/**
 * Shared form body for creating and editing an invoice item. The parent
 * owns the <Form> element and the action; this renders the fields and the
 * hidden organization id.
 */
import { useState } from "react";
import { Input, TextArea } from "../../components/inputs/Input";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import PriceInput from "../../components/inputs/PriceInput";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { InvoiceItem } from "../invoices/type";

interface InvoiceItemFormProps {
  item?: InvoiceItem | null;
  organizationId: number | null;
  events: { id: number; name: string }[];
  children?: React.ReactNode;
}

export const InvoiceItemForm: React.FC<InvoiceItemFormProps> = ({ item, organizationId, events, children }) => {
  const [amount, setAmount] = useState<number>(Number(item?.amount ?? 0));
  return (
    <FormLayout>
      <div className="col-span-6 sm:col-span-4">
        <Input label="Item Name" name="name" required maxLength={120} defaultValue={item?.name ?? ""} />
      </div>
      <div className="col-span-6 sm:col-span-1">
        <Input label="Sort Order" name="sort_order" type="number" defaultValue={item?.sort_order ?? 0} />
      </div>
      <div className="col-span-6 sm:col-span-1 flex items-end">
        <IndeterminateCheckbox label="Is Active?" name="is_active" defaultChecked={item?.is_active ?? true} />
      </div>
      <div className="col-span-6">
        <TextArea
          label="Line Item Description (what the customer sees on the invoice)"
          name="description"
          defaultValue={item?.description ?? ""}
        />
      </div>
      <div className="col-span-6 sm:col-span-2">
        <PriceInput label="Unit Amount" name="amount" value={amount} onChange={(v) => setAmount(v ?? 0)} required />
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Input
          label="Tax % ( 7.5 = 7.5% )"
          name="tax_percentage"
          type="number"
          defaultValue={Number(item?.tax_percentage ?? 0)}
        />
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Input
          label="Service Fee % ( 7.5 = 7.5% )"
          name="service_fee_percentage"
          type="number"
          defaultValue={Number(item?.service_fee_percentage ?? 0)}
        />
      </div>
      <div className="col-span-6">
        <CustomSelect
          title="Event (optional, a label only)"
          name="event_id"
          isClearable
          defaultValue={item?.event_id ?? null}
          options={(events ?? []).map((e) => ({ label: e.name, value: e.id }))}
        />
      </div>
      <input type="hidden" name="organization_id" value={organizationId ?? ""} />
      {children}
    </FormLayout>
  );
};

/** Reads the form body into the columns of `invoice_items`. */
export const readInvoiceItemForm = (body: FormData, userId: string | number | undefined) => {
  const num = (key: string) => {
    const raw = String(body.get(key) ?? "").trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  };
  const eventRaw = String(body.get("event_id") ?? "").trim();
  const orgRaw = String(body.get("organization_id") ?? "").trim();
  return {
    name: String(body.get("name") ?? "").trim(),
    description: String(body.get("description") ?? "").trim() || null,
    amount: num("amount"),
    tax_percentage: num("tax_percentage"),
    service_fee_percentage: num("service_fee_percentage"),
    sort_order: Math.round(num("sort_order")),
    is_active: String(body.get("is_active")) === "true",
    event_id: eventRaw ? parseInt(eventRaw, 10) : null,
    organization_id: orgRaw ? parseInt(orgRaw, 10) : null,
    last_updated_by: userId != null && String(userId).trim() !== "" ? Number(userId) : null,
  };
};

export default InvoiceItemForm;
